import snakeCase from 'lodash/snakeCase';

import { HOMEPAGE_SOURCE, TEMPO } from 'lib/constants';
import { objectToQueryString } from 'services/RequestService/helpers';

// NON-REACT PATHS
export const SIGN_IN_PATH = '/users/sign_in';
export const OUR_MENU_PATH = '/our-menu';
export const FAMILY_MENU_PATH = '/family';

// HOMEPAGE
export const HOMEPAGE_PATH = '/home';

export const homepageRootPath = (params = null) =>
  `${HOMEPAGE_PATH}${params ? `?${objectToQueryString(params)}` : ''}`;

export const homepageMenuPath = (menuSlug, params = null) =>
  `${HOMEPAGE_PATH}/${menuSlug}${params ? `?${objectToQueryString(params)}` : ''}`;

export const homepageDeliveryOptionsPath = () => `${HOMEPAGE_PATH}/delivery-options`;

export const isHomepageDeliveryOptionsPath = pathname => pathname === homepageDeliveryOptionsPath();

export const homepagePastOrderSummaryPath = menuSlug => `${HOMEPAGE_PATH}/${menuSlug}/receipt`;

export const homepagePastReceiptMealDetailsPath = (menuSlug, mealId, activeTab) =>
  `${HOMEPAGE_PATH}/${menuSlug}/receipt/meals/${mealId}${
    activeTab ? `?${objectToQueryString({ activeTab })}` : ''
  }`;

export const homepagePromotionMealDetailsPath = (promotionId, menuSlug, mealId) =>
  `${HOMEPAGE_PATH}/promotions/${promotionId}/${menuSlug}/meals/${mealId}`;

export const homepageShoppableBehavioralDiscountPath = (menuSlug, behavioralDiscountId) =>
  `${HOMEPAGE_PATH}/${menuSlug}/discount_meals/${behavioralDiscountId}`;

export const homepageShoppablePromotionPath = (menuSlug, promotionId) =>
  `${HOMEPAGE_PATH}/${menuSlug}/promotion_meals/${promotionId}`;

// COOKBOOK
export const COOKBOOK_PATH = '/account/cookbook';
export const COOKBOOK_SHOPPING_LIST_PATH = `${COOKBOOK_PATH}/shopping_list`;

export const cookbookMealDetailsPath = mealId => `${COOKBOOK_PATH}?mealId=${mealId}`;

// PICK YOUR MEALS
export const PICK_MEALS_MENU_PATH = '/pick-meals';
export const PICK_MEALS_BILLING_PATH = '/pick-meals/billing/tempo';
export const PICK_MEALS_THANK_YOU_PATH = '/pick-meals/thankyou/tempo';

export const isPickYourMealsExperiencePath = ({ pathname = '', search = '' }) => {
  if (pathname.startsWith(PICK_MEALS_MENU_PATH)) return true;
  if (!pathname.startsWith(OUR_MENU_PATH)) return false;

  return new URLSearchParams(search).get('pym') === 'true';
};

// DASHBOARD
export const DASHBOARD_ROOT_PATH = '/account/calendar';

export const ROOT_PATH_NAME = 'ROOT_PATH';
export const MENU_PATH_NAME = 'MENU_PATH';
export const SUMMARY_PATH_NAME = 'SUMMARY_PATH';
export const MEAL_DETAILS_PATH_NAME = 'MEAL_DETAILS_PATH';
export const DELIVERY_OPTIONS_PATH_NAME = 'DELIVERY_OPTIONS_PATH';
export const PAST_SUMMARY_PATH_NAME = 'PAST_ORDER_SUMMARY_PATH';

export const dashboardRootPath = (params = null) =>
  `${DASHBOARD_ROOT_PATH}${params ? `?${objectToQueryString(params)}` : ''}`;

export const dashboardMenuPath = (menuSlug, params = null) =>
  `${DASHBOARD_ROOT_PATH}/${menuSlug}${params ? `?${objectToQueryString(params)}` : ''}`;

export const dashboardMenuExtrasPath = (menuSlug, params = null) =>
  `${DASHBOARD_ROOT_PATH}/${menuSlug}/extras${params ? `?${objectToQueryString(params)}` : ''}`;

export const dashboardMenuManageDeliveryPath = (menuSlug, params = null) =>
  `${DASHBOARD_ROOT_PATH}/${menuSlug}/manage/delivery-options${params ? `?${objectToQueryString(params)}` : ''}`;

export const dashboardMenuExtrasManageDeliveryPath = (menuSlug, params = null) =>
  `${DASHBOARD_ROOT_PATH}/${menuSlug}/extras/manage/delivery-options${params ? `?${objectToQueryString(params)}` : ''}`;

export const dashboardMenuConfirmationPath = (menuSlug, params = null) =>
  `${DASHBOARD_ROOT_PATH}/${menuSlug}/confirmation${params ? `?${objectToQueryString(params)}` : ''}`;

export const dashboardSummaryPath = menuSlug => `${DASHBOARD_ROOT_PATH}/${menuSlug}/summary`;

export const dashboardPastOrderSummary = menuSlug =>
  `${DASHBOARD_ROOT_PATH}/weekly_baskets/${menuSlug}/past-receipt`;

export const dashboardPastReceiptMealDetailsPath = (menuSlug, mealId, activeTab) =>
  `${dashboardPastOrderSummary(menuSlug)}/meals/${mealId}${
    activeTab ? `?${objectToQueryString({ activeTab })}` : ''
  }`;

export const dashboardDeliveryOptionsPath = menuSlug =>
  `${DASHBOARD_ROOT_PATH}/${menuSlug}/delivery-options`;

export const buildPastReceiptMealDetailsPath = (source, menuSlug, mealId, activeTab) => {
  return source === HOMEPAGE_SOURCE
    ? homepagePastReceiptMealDetailsPath(menuSlug, mealId, activeTab)
    : dashboardPastReceiptMealDetailsPath(menuSlug, mealId, activeTab);
};

// Path Matching Helpers
export const isDashboardPath = (pathname, params) => pathname === dashboardRootPath(params);

export const isMenuPath = (pathname, menuSlug, params) =>
  pathname === dashboardMenuPath(menuSlug, params);

export const isDashboardMenuManageDeliveryPath = (pathname, menuSlug, params) =>
  pathname === dashboardMenuManageDeliveryPath(menuSlug, params);

export const isDashboardMenuExtrasManageDeliveryPath = (pathname, menuSlug, params) =>
  pathname === dashboardMenuExtrasManageDeliveryPath(menuSlug, params);

export const isSummaryPath = (pathname, menuSlug) => pathname === dashboardSummaryPath(menuSlug);

export const isDeliveryOptionsPath = (pathname, menuSlug) =>
  pathname === dashboardDeliveryOptionsPath(menuSlug);

export const isPastOrderSummaryPath = (pathname, menuSlug) => {
  return (
    pathname === dashboardPastOrderSummary(menuSlug) ||
    pathname === homepagePastOrderSummaryPath(menuSlug)
  );
};

export const isPastReceiptMealDetailsPath = (pathname, menuSlug, mealId) => {
  return (
    pathname === dashboardPastReceiptMealDetailsPath(menuSlug, mealId) ||
    pathname === homepagePastReceiptMealDetailsPath(menuSlug, mealId)
  );
};

export const isShoppableBehavioralDiscountPath = (pathname, menuSlug, behavioralDiscountId) => {
  return pathname === homepageShoppableBehavioralDiscountPath(menuSlug, behavioralDiscountId);
};

export const isShoppablePromotionPath = (pathname, menuSlug, promotionId) => {
  return pathname === homepageShoppablePromotionPath(menuSlug, promotionId);
};

export const isPromotionMealDetailsPath = (pathname, promotionId, menuSlug, mealId) => {
  return pathname === homepagePromotionMealDetailsPath(promotionId, menuSlug, mealId);
};

export const isMealDetailsPath = (pathname, mealId, menuSlug) => {
  return (
    (pathname === DASHBOARD_ROOT_PATH || pathname === HOMEPAGE_PATH) &&
    Boolean(mealId) &&
    Boolean(menuSlug)
  );
};

export const isSubscriptionPath = pathname => pathname === ACCOUNT_SUBSCRIPTION_PATH;

export const isDashboardExtrasPath = (pathname, menuSlug, params = null) =>
  pathname === dashboardMenuExtrasPath(menuSlug, params);

// ACCOUNT
const ACCOUNT_SETTINGS_PATH = '/account/settings';
export const ACCOUNT_PATH = '/account';
export const ACCOUNT_ORDERS_PATH = '/account/order-history';
export const ACCOUNT_ORDERS_SUMMARY_PATH = '/account/order-history/:menuId';
export const ACCOUNT_SETTINGS_PAUSE_REASONS_PATH = `${ACCOUNT_SETTINGS_PATH}/pause-reasons`;
export const ACCOUNT_SETTINGS_PAUSES_PATH = `${ACCOUNT_SETTINGS_PATH}/pause`;
export const ACCOUNT_EDIT_PATH = `${ACCOUNT_SETTINGS_PATH}/edit`;
export const ACCOUNT_SUBSCRIPTION_PATH = `${ACCOUNT_SETTINGS_PATH}/subscriptions`;
export const ACCOUNT_PREFERENCES_PATH = `${ACCOUNT_SETTINGS_PATH}/taste-profile`;
export const ACCOUNT_PAYMENT_PATH = `${ACCOUNT_SETTINGS_PATH}/billing`;
export const ACCOUNT_SCHEDULE_RESTART = '/account/pause/scheduled-restart/new';
export const ACCOUNT_NOTIFICATIONS_PATH = `${ACCOUNT_SETTINGS_PATH}/notifications`;
export const ACCOUNT_CCPA_REQUEST_PATH = `${ACCOUNT_SETTINGS_PATH}/privacy-request`;
export const ACCOUNT_RESTART_PATH = `${ACCOUNT_SETTINGS_PATH}/reactivate`;
export const ACCOUNT_VERIFY_PATH = '/account/verify';
export const ACCOUNT_VERIFY_PAYMENT_PATH = `${ACCOUNT_VERIFY_PATH}/payment`;
export const ACCOUNT_VERIFY_ADDRESS_PATH = `${ACCOUNT_VERIFY_PATH}/address`;
export const ACCOUNT_REACTIVATE_OFFER_PATH = '/account/reactivate/offer';
export const BOX_REACTIVATIONS_PATH = '/account/reactivate/box';
export const OFFER_REWARDS_PATH = '/account/rewards';
export const FAQ = '/faq';
export const SUPPORT_REQUEST = '/support-request';
export const REACTIVATION_HUB = '/reactivate';

export const TEMPO_SUPPORT_REQUEST = 'https://support.tempomeals.com/hc/en-us/requests/new';

export const railsPauseDeflectorRoute = reason =>
  `/account/pause/reason/new?reason=${snakeCase(reason)}`;

export const pauseSuccessPath = frequencySubscriptionChangeId =>
  `/account/pause/reason/${frequencySubscriptionChangeId}`;

// SEND A BOX
const SEND_A_BOX_ROOT_PATH = '/send-a-box';
const SEND_A_BOX_RECIPIENT_PATH = `${SEND_A_BOX_ROOT_PATH}/recipients`;

const GIFT_CARD_PATH = '/gift-cards';

export const sendABoxRootPath = (params = null) =>
  `${SEND_A_BOX_ROOT_PATH}${params ? `?${objectToQueryString(params)}` : ''}`;

export const sendABoxGifterEditPath = boxHashId => `${SEND_A_BOX_ROOT_PATH}/${boxHashId}/edit`;
export const sendABoxCheckoutPath = boxHashId =>
  `${SEND_A_BOX_ROOT_PATH}/${boxHashId}/order-summary`;
export const sendABoxCheckoutConfirmationPath = boxHashId =>
  `${SEND_A_BOX_ROOT_PATH}/${boxHashId}/confirmation`;

export const sendABoxGiftCardPath = () => GIFT_CARD_PATH;

export const sendABoxRecipientBoxViewPath = boxHashId =>
  `${SEND_A_BOX_RECIPIENT_PATH}/${boxHashId}/view`;
export const sendABoxRecipientBoxEditPath = (boxHashId, token) =>
  `${SEND_A_BOX_RECIPIENT_PATH}/${boxHashId}/edit${token ? `?${objectToQueryString({ token })}` : ''}`;
export const sendABoxRecipientBoxConfirmationPath = (boxHashId, token) =>
  `${SEND_A_BOX_RECIPIENT_PATH}/${boxHashId}/confirmation${token ? `?${objectToQueryString({ token })}` : ''}`;

// Funnel V2
export const INTRO_PATH = '/signup/intro';
export const PICK_PLAN_PATH = '/signup/plans';
export const PREFERENCE_DIETARY = '/signup/preference/dietary';
const PREFERENCE_AVOID = '/signup/preference/avoid';
export const PROMO_PATH = '/signup/promo';
export const BOX_PATH = '/signup/box';
const CHECKOUT_PATH = '/signup/billing';
const THANK_YOU_PATH = '/signup/thankyou';

// Pick Your Meals - using exported constants directly

// Box Recommender
export const BOX_RECOMMENDER_PEOPLE = '/signup/box-people';
export const BOX_RECOMMENDER_MEALS = '/signup/box-meals';
export const BOX_RECOMMENDER_DAYS = '/signup/box-days';
export const getBrandRoute = ({ brand, isFamily = false, addSuffix = true }) => {
  let routeSuffix = '';

  if (addSuffix) {
    if (isFamily && brand !== TEMPO) {
      routeSuffix = '/family';
    } else {
      routeSuffix = brand === TEMPO ? '/tempo' : '/standard';
    }
  }

  return {
    intro: `${INTRO_PATH}${routeSuffix}`,
    pickPlan: `${PICK_PLAN_PATH}${routeSuffix}`,
    preferenceDietary: `${PREFERENCE_DIETARY}${routeSuffix}`,
    preferenceAvoid: `${PREFERENCE_AVOID}${routeSuffix}`,
    promo: `${PROMO_PATH}${routeSuffix}`,
    box: `${BOX_PATH}${routeSuffix}`,
    boxRecommenderPeople: `${BOX_RECOMMENDER_PEOPLE}${routeSuffix}`,
    boxRecommenderMeals: `${BOX_RECOMMENDER_MEALS}${routeSuffix}`,
    boxRecommenderDays: `${BOX_RECOMMENDER_DAYS}${routeSuffix}`,
    billing: `${CHECKOUT_PATH}${routeSuffix}`,
    thankyou: `${THANK_YOU_PATH}${routeSuffix}`,
    pickMeals: PICK_MEALS_MENU_PATH,
    pickMealsBilling: PICK_MEALS_BILLING_PATH,
    pickMealsThankYou: PICK_MEALS_THANK_YOU_PATH,
  };
};

export function getRoutesMap(brand, isFamily) {
  const brandRoutes = getBrandRoute({ brand, isFamily });

  return {
    onPlansStep: brandRoutes.pickPlan,
    onPreferenceDietaryStep: brandRoutes.preferenceDietary,
    onPreferenceAvoidStep: brandRoutes.preferenceAvoid,
    onPromoStep: brandRoutes.promo,
    onBuildABoxStep: brandRoutes.box,
    onBoxRecommenderPeopleStep: brandRoutes.boxRecommenderPeople,
    onBoxRecommenderMealsStep: brandRoutes.boxRecommenderMeals,
    onBoxRecommenderDaysStep: brandRoutes.boxRecommenderDays,
    onBillingStep: brandRoutes.billing,
    onThankyouStep: brandRoutes.thankyou,
    onPickMealsStep: brandRoutes.pickMeals,
    onPickMealsBillingStep: brandRoutes.pickMealsBilling,
    onPickMealsThankYouStep: brandRoutes.pickMealsThankYou,
  };
}
